// client/src/components/pages/RouteIntelligence/PreloadMonitorCard.tsx (converted from .js, ST-00229)
//
// ANL-9: Run, the settings cog and the Settings/Delete menu need route_intelligence.manage
// (canManage); without it the card shows Details only.
import React, { useState } from 'react';
import { Play, Settings, Eye, MoreVertical, Trash2, Edit2 } from 'lucide-react';
import { getPreloadState, formatDate, formatThreshold } from './preloadConfig';
import type { PreloadDashboardMonitor } from '../../../types/clientPages/routeIntelligence';

interface PreloadMonitorCardProps {
  monitor: PreloadDashboardMonitor;
  onViewDetails: (monitor: PreloadDashboardMonitor) => void;
  onRunCheck: (monitor: PreloadDashboardMonitor) => void;
  onEditMonitor: (monitor: PreloadDashboardMonitor) => void;
  onDeleteMonitor: (monitorId: number) => void;
  isRunning: boolean;
  /** route_intelligence.manage: show Run, settings and delete. */
  canManage?: boolean;
  delay?: number;
}

const PreloadMonitorCard = React.memo(
  ({
    monitor,
    onViewDetails,
    onRunCheck,
    onEditMonitor,
    onDeleteMonitor,
    isRunning,
    canManage = false,
    delay = 0,
  }: PreloadMonitorCardProps) => {
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const state = getPreloadState(monitor.latest_preload_count, monitor.alert_threshold);
    const StateIcon = state.icon;

    return (
      <div
        className="bg-surface rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 overflow-hidden"
        style={{ animationDelay: `${delay}ms` }}
      >
        <div className="flex">
          <div className={`w-1 flex-shrink-0 ${state.dot}`} />
          <div className="flex-1 p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-800 truncate">
                  {monitor.grouping_name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${state.badgeBg} ${state.badgeText} border ${state.badgeBorder}`}
                  >
                    <StateIcon className="w-3 h-3" /> {state.label}
                  </span>
                  {monitor.latest_date && (
                    <span className="text-[11px] text-gray-400">
                      {formatDate(monitor.latest_date)}
                    </span>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="relative ml-2">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    aria-label="Monitor actions"
                    className="p-1 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-50"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {showMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 w-36 bg-surface rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            onEditMonitor(monitor);
                          }}
                          className="w-full px-3 py-1.5 text-left text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Edit2 className="w-3 h-3" /> Settings
                        </button>
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            setShowDeleteConfirm(true);
                          }}
                          className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Preload count display */}
            <div
              className="text-2xl font-bold tabular-nums tracking-tight mb-1"
              style={{ color: state.accent }}
            >
              {monitor.latest_preload_count != null ? monitor.latest_preload_count : '--'}
              <span className="text-sm font-medium text-gray-400 ml-1">
                preload{monitor.latest_preload_count !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3">
              <span>
                30d{' '}
                <span className="font-semibold text-gray-500">
                  {monitor.total_preloads_in30 || 0}
                </span>
              </span>
              <span className="text-gray-200">|</span>
              <span>{monitor.preload_days_in30 || 0} days</span>
              <span className="text-gray-200">|</span>
              <span>{formatThreshold(monitor.time_threshold_minutes)}</span>
            </div>

            {/* Ticket count */}
            {(monitor.latest_total_tickets ?? 0) > 0 && (
              <div className="text-[11px] text-gray-300 tabular-nums mb-3">
                {monitor.latest_total_tickets} total tickets
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              <button
                onClick={() => onViewDetails(monitor)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 text-xs font-medium text-gray-600 hover:text-slate-800 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Eye className="w-3.5 h-3.5" /> Details
              </button>
              {canManage && (
                <button
                  onClick={() => onRunCheck(monitor)}
                  disabled={isRunning}
                  className="inline-flex items-center justify-center gap-1 h-8 px-3 text-xs font-semibold text-white rounded-md transition-colors disabled:opacity-50 bg-primary-500"
                >
                  <Play className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`} />
                  {isRunning ? 'Running' : 'Run'}
                </button>
              )}
              {canManage && (
                <button
                  onClick={() => onEditMonitor(monitor)}
                  aria-label="Monitor settings"
                  className="inline-flex items-center justify-center h-8 w-8 text-gray-300 hover:text-gray-500 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-5 max-w-sm mx-4 shadow-xl border border-gray-200">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Delete Monitor</h3>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Delete <strong>{monitor.grouping_name}</strong> and all associated results, tickets,
                and alerts?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onDeleteMonitor(monitor.monitor_id);
                  }}
                  className="px-3 py-1.5 text-xs text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

PreloadMonitorCard.displayName = 'PreloadMonitorCard';
export default PreloadMonitorCard;
